<script lang="ts">
  import type { Distributor } from "@shared/types/Distributor";
  import type { SalesRep } from "@shared/types/SalesRep";
  import formatPhone from "@shared/filters/phone.js";
  import SalesRepAutocompleteDropdown from "./AutocompleteDropdown.svelte";
  import Autocomplete from "@shared/svelte/Components/Autocomplete.svelte";
  import CustomerServiceCheckbox from "@shared/svelte/Components/CustomerServiceCheckbox.svelte";
  import AddRep from "./AddRep.svelte";
  import SelectedSalesRep from "./SelectedSalesRep.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { SalesRepsEventEntity } from "@shared/services/event_tracking/entities";

  export let distributor: Distributor;
  export let selectedReps: any[] = distributor.sales_reps || [];
  export let addSalesRepErrorCallback;

  const { trackClick, trackEvent } = EventTracking;
  let ordersFromCustomerService: boolean = selectedReps.some(
    (rep: SalesRep) => rep.id == distributor.customer_service_rep_id
  );
  let addingRep: boolean = false;
  let loading: boolean = false;

  const syncReps = (shouldLoad: boolean = true) => {
    if (shouldLoad) {
      loading = true;
    }
    const options = {
      method: "POST",
      url: `/api/retailer/distributors/${distributor.id}/sales_rep_assignments/replace`,
      data: {
        replace: {
          sales_rep_ids: selectedReps.map(rep => rep.id),
        },
      },
    };
    return HttpPromise(options).then(r => {
      loading = false;
      trackEvent(
        "Sales Reps Added",
        new SalesRepsEventEntity({
          service: "Onboarding2",
          component: "RepsPerDistributor",
          ids: selectedReps.map(rep => rep.id),
        })
      );
      return Promise.resolve(r);
    });
  };

  const addSalesRep = (element: any) => {
    const current: SalesRep = element.detail;
    if (typeof current == "object" && !selectedReps.find((rep: SalesRep) => rep.id == current.id)) {
      selectedReps = [...selectedReps, current];
      syncReps();
    }
  };

  const toggleAddingRep = () => {
    addingRep = true;
  };

  const removeSalesRep = (salesRepToRemove: SalesRep) => {
    selectedReps = selectedReps.filter(rep => rep.id !== salesRepToRemove.id);
    syncReps();
  };

  const useCustomerService = e => {
    if (e.target.checked) {
      selectedReps = [...selectedReps, { id: distributor.customer_service_rep_id }];
    } else {
      selectedReps = selectedReps.filter(rep => rep.id !== distributor.customer_service_rep_id);
    }
    syncReps(false);
  };

</script>

<style>
  .onboarding-reps-per-distributor :global(.svelte-tags-input-matchs) {
    position: static;
  }
  .onboarding-reps-per-distributor :global(.sales-rep-info) {
    max-width: 68vw;
    word-break: break-all;
  }

</style>

<div
  distributor-id={distributor.id}
  class="fizz-card fizz-radius-medium fizz-bordered fizz-stack-16 onboarding-reps-per-distributor"
>
  <div class="fizz-flex-row-8 fizz-inset-square-16 fizz-border-bottom">
    <Icon type="TruckCircle" height="32" width="32" extraClassNames="color-brandSecondary" />
    <strong class="fizz-text-medium fizz-flex-item">{distributor.name}</strong>
    {#if distributor.contact_phone}
      <p class="fizz-text-small">Contact: {formatPhone(distributor.contact_phone)}</p>
    {/if}
  </div>

  <form class="fizz-flow-16 fizz-inset-square-16 fizz-stack-4">
    <div class="fizz-relative">
      <label class="fizz-label fizz-stack-4" for="search">Add reps</label>
      {#if !addingRep}
        <Autocomplete
          disabled={loading}
          placeholder="Enter a name, email, or phone number"
          objectType="sales_rep"
          searchUrlInput={`/api/retailer/distributors/${distributor.id}/sales_reps`}
          on:tagAdded={addSalesRep}
          on:focus={() => trackClick("onboarding_autocomplete_sales_reps_field")}
          displayTags={false}
          listMode={true}
          dropdownItemTemplateComponent={SalesRepAutocompleteDropdown}
          dropdownClassName="fizz-card-floating fizz-radius-medium"
        >
          <div slot="dropdown-header">
            <p class="fizz-text-subdued fizz-inset-squish-16"><strong>Suggestions</strong></p>
          </div>
          <div slot="dropdown-footer">
            <a
              on:click|preventDefault={toggleAddingRep}
              href="/"
              class="fizz-text-strong fizz-highlight-on-hover fizz-inset-squish-16">+ Add New</a
            >
          </div>
        </Autocomplete>
      {/if}
    </div>
    {#if addingRep}
      <AddRep bind:addingRep {distributor} on:repAdded={addSalesRep} {addSalesRepErrorCallback} />
    {/if}
    {#if distributor.customer_service_rep_id}
      <CustomerServiceCheckbox
        {ordersFromCustomerService}
        distributorId={distributor.id}
        {useCustomerService}
      />
    {/if}
  </form>

  {#if selectedReps.length > 0}
    {#each selectedReps.filter(rep => rep.id !== distributor.customer_service_rep_id) as salesRep}
      <SelectedSalesRep {salesRep} {removeSalesRep} />
    {/each}
  {/if}
</div>
